/**
 * Hook for fetching field types from the backend schema.
 */

import { useQuery } from '@tanstack/react-query';

import { useOrg } from '@/contexts/OrgContext';

import { createSchemaClient, type FieldType } from '../core/SchemaClient';

/**
 * Hook to fetch all field types from the backend schema.
 *
 * The data is cached per org for efficient reuse across components.
 * Field types rarely change, so they are cached for 5 minutes.
 *
 * @returns React Query result with field types array
 *
 * @example
 * const { data: fieldTypes, isLoading, error } = useFieldTypes();
 */
export function useFieldTypes() {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;

  return useQuery({
    queryKey: ['fieldTypes', orgId],
    queryFn: async () => {
      if (!orgId) {
        throw new Error('No organization selected');
      }
      const client = createSchemaClient(orgId);
      return client.getFieldTypes();
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000, // Field types rarely change, cache for 5 minutes
  });
}

/**
 * Hook to get a single field type by name.
 *
 * Uses the useFieldTypes hook internally and finds the matching field type.
 *
 * @param fieldTypeName - The name of the field type to find (e.g., 'text', 'number', 'currency')
 * @returns The field type if found, undefined otherwise
 *
 * @example
 * const currencyFieldType = useFieldType('currency');
 * console.log(currencyFieldType?.units); // Available currency units
 */
export function useFieldType(fieldTypeName: string): FieldType | undefined {
  const { data: fieldTypes } = useFieldTypes();
  return fieldTypes?.find(ft => ft.field_type_name === fieldTypeName);
}
